import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Truck, MapPin, Calendar, Loader2, CheckCircle, ArrowRight } from 'lucide-react';
import axios from 'axios';
import { useToast } from '../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Commander = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [step, setStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const [formData, setFormData] = useState({
    type: '',
    name: '',
    email: '',
    phone: '',
    postalCode: '',
    adresseChantier: '',
    serviceType: 'benne',
    benneVolume: '',
    benneType: '',
    dateDebut: '',
    notes: ''
  });

  const [calculatedPrice, setCalculatedPrice] = useState(null);

  // Calculer le prix en temps réel
  useEffect(() => {
    if (formData.benneVolume && formData.benneType) {
      const price = calculatePrice(formData.benneVolume, formData.benneType);
      setCalculatedPrice(price);
    } else {
      setCalculatedPrice(null);
    }
  }, [formData.benneVolume, formData.benneType]);

  const calculatePrice = (volume, type) => {
    const prices = {
      "3m³": { "Gravats": 245 },
      "8m³": { "Gravats": 300, "Mélange (DIB)": 400 },
      "10m³": { "Gravats": 350, "Mélange (DIB)": 480 },
      "15m³": { "Gravats": null, "Mélange (DIB)": 675 },
      "20m³": { "Gravats": null, "Mélange (DIB)": 860 },
      "30m³": { "Gravats": null, "Mélange (DIB)": 1185 }
    };
    
    if (volume in prices && type in prices[volume] && prices[volume][type]) {
      return prices[volume][type];
    }
    return null;
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      const response = await axios.post(`${API}/devis`, formData);
      
      toast({
        title: "Commande envoyée !",
        description: "Nous vous recontacterons dans les 2 heures.",
      });
      
      // Rediriger vers la page de paiement
      setTimeout(() => {
        navigate(`/paiement/${response.data.id}`);
      }, 1500);
      
    } catch (error) {
      console.error('Error:', error);
      toast({
        title: "Erreur",
        description: "Une erreur est survenue. Veuillez réessayer.",
        variant: "destructive"
      });
      setIsSubmitting(false);
    }
  };

  const canGoNext = () => {
    if (step === 1) {
      return formData.benneVolume && formData.benneType;
    }
    if (step === 2) {
      return formData.postalCode && formData.adresseChantier && formData.dateDebut;
    }
    if (step === 3) {
      return formData.type && formData.name && formData.email && formData.phone;
    }
    return false;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-green-50 py-12 px-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-2">
            Commander une Benne
          </h1>
          <p className="text-gray-600">Location Benne Chantier - Livraison en 24h</p>
        </div>

        {/* Progress Steps */}
        <div className="flex justify-center mb-12">
          <div className="flex items-center space-x-4">
            {[
              { num: 1, label: 'Benne' },
              { num: 2, label: 'Livraison' },
              { num: 3, label: 'Coordonnées' }
            ].map((s, index) => (
              <React.Fragment key={s.num}>
                <div className={`flex items-center ${step >= s.num ? 'text-green-600' : 'text-gray-400'}`}>
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${
                    step >= s.num ? 'bg-green-600 text-white' : 'bg-gray-200 text-gray-600'
                  }`}>
                    {step > s.num ? <CheckCircle className="h-6 w-6" /> : s.num}
                  </div>
                  <span className="ml-2 font-semibold hidden sm:inline">{s.label}</span>
                </div>
                {index < 2 && (
                  <div className={`w-16 h-1 ${step > s.num ? 'bg-green-600' : 'bg-gray-200'}`}></div>
                )}
              </React.Fragment>
            ))}
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Formulaire principal */}
          <div className="lg:col-span-2">
            <Card className="p-8">
              {/* Étape 1: Choix de la benne */}
              {step === 1 && (
                <div className="space-y-6">
                  <div className="flex items-center mb-6">
                    <Truck className="h-6 w-6 text-green-600 mr-2" />
                    <h2 className="text-2xl font-bold text-gray-900">Choisissez votre benne</h2>
                  </div>

                  <div>
                    <Label className="text-lg mb-3 block">Volume de la benne *</Label>
                    <div className="grid sm:grid-cols-2 gap-4">
                      {['3m³', '8m³', '10m³', '15m³', '20m³', '30m³'].map((vol) => (
                        <button
                          key={vol}
                          onClick={() => setFormData({ ...formData, benneVolume: vol, benneType: vol === '3m³' ? 'Gravats' : formData.benneType })}
                          className={`p-4 border-2 rounded-lg text-left transition-all ${
                            formData.benneVolume === vol
                              ? 'border-green-600 bg-green-50'
                              : 'border-gray-200 hover:border-green-300'
                          }`}
                        >
                          <div className="font-bold text-lg">{vol}</div>
                          <div className="text-sm text-gray-600">
                            {vol === '8m³' && 'Petit chantier'}
                            {vol === '10m³' && 'Moyen chantier'}
                            {vol === '15m³' && 'Grand chantier'}
                            {vol === '20m³' && 'Très grand chantier'}
                            {vol === '30m³' && 'Projet industriel'}
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <Label className="text-lg mb-3 block">Type de déchets *</Label>
                    <div className="grid sm:grid-cols-2 gap-4">
                      {(formData.benneVolume === '3m³' ? ['Gravats'] : ['Gravats', 'Mélange (DIB)']).map((type) => {
                        const isDisabled = formData.benneVolume && 
                          ['15m³', '20m³', '30m³'].includes(formData.benneVolume) && 
                          type === 'Gravats';
                        
                        return (
                          <button
                            key={type}
                            onClick={() => !isDisabled && setFormData({ ...formData, benneType: type })}
                            disabled={isDisabled}
                            className={`p-4 border-2 rounded-lg text-left transition-all ${
                              formData.benneType === type
                                ? 'border-green-600 bg-green-50'
                                : isDisabled
                                ? 'border-gray-200 bg-gray-100 opacity-50 cursor-not-allowed'
                                : 'border-gray-200 hover:border-green-300'
                            }`}
                          >
                            <div className="font-bold">{type}</div>
                            <div className="text-sm text-gray-600">
                              {type === 'Gravats' && 'Béton, pierres, tuiles'}
                              {type === 'Mélange (DIB)' && 'Bois, plâtre, plastique'}
                            </div>
                            {isDisabled && (
                              <Badge className="mt-2 bg-red-100 text-red-700">Non disponible pour ce volume</Badge>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}

              {/* Étape 2: Livraison */}
              {step === 2 && (
                <div className="space-y-6">
                  <div className="flex items-center mb-6">
                    <MapPin className="h-6 w-6 text-green-600 mr-2" />
                    <h2 className="text-2xl font-bold text-gray-900">Informations de livraison</h2>
                  </div>

                  <div>
                    <Label htmlFor="adresseChantier">Adresse complète du chantier *</Label>
                    <Input
                      id="adresseChantier"
                      className="mt-2"
                      value={formData.adresseChantier}
                      onChange={(e) => setFormData({ ...formData, adresseChantier: e.target.value })}
                      placeholder="12 rue Example, 75001 Paris"
                    />
                  </div>

                  <div>
                    <Label htmlFor="postalCode">Code postal *</Label>
                    <Input
                      id="postalCode"
                      className="mt-2"
                      value={formData.postalCode}
                      onChange={(e) => setFormData({ ...formData, postalCode: e.target.value })}
                      placeholder="75001"
                    />
                  </div>

                  <div>
                    <Label htmlFor="dateDebut">Date de début souhaitée *</Label>
                    <Input
                      id="dateDebut"
                      type="date"
                      className="mt-2"
                      value={formData.dateDebut}
                      onChange={(e) => setFormData({ ...formData, dateDebut: e.target.value })}
                      min={new Date().toISOString().split('T')[0]}
                    />
                    <p className="text-sm text-gray-500 mt-1">Livraison possible dès 24h après commande</p>
                  </div>

                  <div>
                    <Label htmlFor="notes">Instructions particulières (optionnel)</Label>
                    <Textarea
                      id="notes"
                      className="mt-2"
                      value={formData.notes}
                      onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                      placeholder="Accès difficile, code d'entrée, etc..."
                      rows={3}
                    />
                  </div>
                </div>
              )}

              {/* Étape 3: Coordonnées */}
              {step === 3 && (
                <div className="space-y-6">
                  <div className="flex items-center mb-6">
                    <CheckCircle className="h-6 w-6 text-green-600 mr-2" />
                    <h2 className="text-2xl font-bold text-gray-900">Vos coordonnées</h2>
                  </div>

                  <div>
                    <Label>Vous êtes *</Label>
                    <Select value={formData.type} onValueChange={(value) => setFormData({ ...formData, type: value })}>
                      <SelectTrigger className="mt-2">
                        <SelectValue placeholder="Sélectionnez" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="particulier">Un particulier</SelectItem>
                        <SelectItem value="professionnel">Un professionnel</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="name">Nom et prénom *</Label>
                    <Input
                      id="name"
                      className="mt-2"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="Votre nom et prénom"
                    />
                  </div>

                  <div>
                    <Label htmlFor="email">Email *</Label>
                    <Input
                      id="email"
                      type="email"
                      className="mt-2"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      placeholder="votre@email.fr"
                    />
                  </div>

                  <div>
                    <Label htmlFor="phone">Téléphone *</Label>
                    <Input
                      id="phone"
                      type="tel"
                      className="mt-2"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      placeholder="06 12 34 56 78"
                    />
                  </div>
                </div>
              )}

              {/* Navigation buttons */}
              <div className="flex justify-between mt-8 pt-6 border-t">
                {step > 1 && (
                  <Button
                    variant="outline"
                    onClick={() => setStep(step - 1)}
                  >
                    Retour
                  </Button>
                )}
                
                {step < 3 ? (
                  <Button
                    onClick={() => setStep(step + 1)}
                    disabled={!canGoNext()}
                    className="ml-auto bg-green-600 hover:bg-green-700"
                  >
                    Continuer
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                ) : (
                  <Button
                    onClick={handleSubmit}
                    disabled={!canGoNext() || isSubmitting}
                    className="ml-auto bg-green-600 hover:bg-green-700 py-6"
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                        Envoi en cours...
                      </>
                    ) : (
                      <>
                        Valider la commande
                        <CheckCircle className="ml-2 h-5 w-5" />
                      </>
                    )}
                  </Button>
                )}
              </div>
            </Card>
          </div>

          {/* Récapitulatif */}
          <div className="lg:col-span-1">
            <Card className="p-6 sticky top-6">
              <h3 className="text-xl font-bold mb-4 text-gray-900">Récapitulatif</h3>
              
              <div className="space-y-4">
                {formData.benneVolume && (
                  <div className="flex justify-between py-2 border-b">
                    <span className="text-gray-600">Benne</span>
                    <span className="font-semibold">{formData.benneVolume}</span>
                  </div>
                )}
                
                {formData.benneType && (
                  <div className="flex justify-between py-2 border-b">
                    <span className="text-gray-600">Type</span>
                    <span className="font-semibold">{formData.benneType}</span>
                  </div>
                )}
                
                {formData.adresseChantier && (
                  <div className="py-2 border-b">
                    <span className="text-gray-600 block mb-1">Adresse</span>
                    <span className="font-semibold text-sm">{formData.adresseChantier}</span>
                  </div>
                )}
                
                {formData.dateDebut && (
                  <div className="flex justify-between py-2 border-b">
                    <span className="text-gray-600">Date</span>
                    <span className="font-semibold">{new Date(formData.dateDebut).toLocaleDateString('fr-FR')}</span>
                  </div>
                )}

                {calculatedPrice && (
                  <div className="bg-green-50 p-4 rounded-lg mt-4">
                    <div className="text-sm text-gray-600 mb-1">Prix total HT</div>
                    <div className="text-3xl font-bold text-green-600">{calculatedPrice}€</div>
                    <div className="text-xs text-gray-500 mt-2">
                      Forfait tout compris : location 15 jours + transport + traitement
                    </div>
                  </div>
                )}
              </div>

              <div className="mt-6 pt-6 border-t">
                <div className="flex items-start space-x-2 text-sm text-gray-600">
                  <Calendar className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                  <p>Livraison en <strong>24h</strong> après validation</p>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Commander;
